import React from "react";
import PropTypes from "prop-types";
import { Oval } from "react-loader-spinner";
import { useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import Input from "components/input";
import { useTranslation } from "react-i18next";
import Select from "components/select";
import usePersonalInformation from "./usePersonalInformation";

function EditPersonalInformation({
  onDiscard = () => {},
  onSave = () => {},
  isLoading = false,
}) {
  const [t] = useTranslation();
  const userDetails = useSelector((state) => state.userDetails || {});
  const {
    firstName,
    lastName,
    userNationalities,
    country,
    city,
    email,
    phone,
  } = userDetails;

  const defaultsValues = {
    firstName: firstName || "",
    lastName: lastName || "",
    nationalities: userNationalities.map((elem) => elem.id),
    phoneNumber: phone || "",
    email: email,
    country: country?.id || "",
    city: city?.id || "",
  };

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: defaultsValues,
  });

  const countryId = watch("country");

  const { nationalitiesOptions, countriesOptions, cities, citiesOptions } =
    usePersonalInformation({ countryId });

  const genderOptions = [
    { value: "male", label: t("male") || "Male" },
    { value: "female", label: t("female") || "Female" },
  ];

  return (
    <form
      className="flex flex-col self-stretch rounded-[16px] border-[1px] border-solid border-primary"
      onSubmit={handleSubmit(onSave)}
    >
      <div className="flex p-[24px] justify-between align-center self-stretch border-b border-solid border-[#E4E4E7]">
        <div className="text-[#1D1B22] font-semibold text-[24px]">
          {t("personalInformation")}
        </div>

        <div className="flex items-center gap-[12px]">
          <button
            type="button"
            onClick={onDiscard}
            disabled={isLoading}
            className="cursor-pointer flex py-[14px] px-[16px] gap-[8px] justify-center items-center rounded-[6px] border-[1px] border-solid border-default-color bg-white h-[46px]"
          >
            <div className="text-base-black font-bold text-[16px] not-italic">
              {t("Cancel")}
            </div>
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="cursor-pointer flex py-[14px] px-[16px] gap-[8px] justify-center items-center rounded-[6px] border-[1px] border-solid border-primary bg-primary h-[46px]"
          >
            <Oval
              height={30}
              width={30}
              color="#FFF"
              visible={isLoading}
              ariaLabel="oval-loading"
              secondaryColor="#FFF"
              strokeWidth={5}
              strokeWidthSecondary={3}
            />
            <div className="text-white font-bold text-[16px] not-italic">
              {t("saveChangesCta")}
            </div>
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-[20px] p-[24px] self-stretch">
        {/* ************** FIRST NAME ***************/}
        <Input
          label={t("firstName")}
          isRequired={true}
          placeholder={t("placeHolderType", {
            title: t("firstName").toLowerCase(),
          })}
          name="firstName"
          register={register}
          registerArgs={{
            required: t("errorField", { field: t("firstName") }),
          }}
          errorMessage={errors.firstName ? errors.firstName.message : ""}
          labelClassName="text-primary font-semibold text-[16px]"
          inputClassName={`flex h-[43px] min-w-[250px] sm-max:w-full items-center gap-[8px] self-stretch rounded-[6px] border-[1px] border-solid ${
            errors.firstName ? "border-red" : "border-default-color"
          } bg-transparent outline-none text-dark-gray font-regular text-[16px] not-italic placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal py-[16px] px-[14px]`}
        />

        {/* ************** LAST NAME ***************/}
        <Input
          label={t("lastName")}
          isRequired={true}
          placeholder={t("placeHolderType", {
            title: t("lastName").toLowerCase(),
          })}
          name="lastName"
          register={register}
          registerArgs={{
            required: t("errorField", { field: t("lastName") }),
          }}
          errorMessage={errors.lastName ? errors.lastName.message : ""}
          labelClassName="text-primary font-semibold text-[16px]"
          inputClassName={`flex h-[43px] min-w-[250px] sm-max:w-full items-center gap-[8px] self-stretch rounded-[6px] border-[1px] border-solid ${
            errors.lastName ? "border-red" : "border-default-color"
          } bg-transparent outline-none text-dark-gray font-regular text-[16px] not-italic placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal py-[16px] px-[14px]`}
        />

        {/* ************** NATIONALITY ***************/}
        <Select
          label={t("nationality")}
          placeholder={t("placeholderSelect", {
            field: t("nationality").toLowerCase(),
          })}
          name="nationalities"
          containerClassName="flex flex-col gap-[10px] min-w-[250px] sm-max:w-full"
          isMulti={true}
          closeMenuOnSelect={false}
          value={
            watch("nationalities")
              ? nationalitiesOptions.filter((elem) =>
                  watch("nationalities").includes(elem.id)
                )
              : null
          }
          onChange={(e) => {
            setValue(
              "nationalities",
              e.map((e) => e.value)
            );
          }}
          register={register}
          options={nationalitiesOptions}
        />

        {/* ************** PHONE NUMBER ***************/}
        <Input
          label={t("phoneNumber")}
          placeholder={t("placeholder", {
            field: t("phoneNumber").toLowerCase(),
          })}
          type="text"
          name="phoneNumber"
          register={register}
          labelClassName="text-primary font-semibold text-[16px]"
          inputClassName="flex h-[43px] min-w-[250px] sm-max:w-full items-center gap-[8px] self-stretch rounded-[6px] border-[1px] border-solid border-default-color bg-transparent outline-none text-dark-gray font-regular text-[16px] not-italic placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal py-[16px] px-[14px] no-arrows-on-input"
        />

        {/* ************** COUNTRY ***************/}
        <Select
          label={t("personalInformationCountry")}
          placeholder={t("placeholderSelect", {
            field: t("country").toLowerCase(),
          })}
          name="country"
          containerClassName="flex flex-col gap-[10px] min-w-[250px] sm-max:w-full"
          value={
            watch("country")
              ? countriesOptions.find((elem) => elem.id === watch("country"))
              : null
          }
          onChange={(e) => {
            setValue("country", e.value);
            setValue("city", ""); // Reset city when country changes
          }}
          register={register}
          options={countriesOptions}
        />

        {/* ************** CITY ***************/}
        <Select
          label={t("personalInformationCity")}
          placeholder={t("placeholderSelect", {
            field: t("city").toLowerCase(),
          })}
          name="city"
          containerClassName="flex flex-col gap-[10px] min-w-[250px] sm-max:w-full"
          isDisabled={cities.length <= 0}
          value={
            watch("city")
              ? citiesOptions.find((elem) => elem.id === watch("city"))
              : null
          }
          onChange={(e) => {
            setValue("city", e.value);
          }}
          register={register}
          options={citiesOptions}
        />
      </div>
    </form>
  );
}

EditPersonalInformation.propTypes = {
  onDiscard: PropTypes.func,
  onSave: PropTypes.func,
  isLoading: PropTypes.bool,
};

export default EditPersonalInformation;
